'use client';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { CommonCollectionTypeAttributes, EntityCollection } from '@peerless/data-access/strapi';
import { PreviewCard } from './PreviewCard';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from '@peerless/ui-assets/icons';
import { useMemo } from 'react';

interface Props {
  previews: EntityCollection<CommonCollectionTypeAttributes>;
  urlLabel: string;
  icon: IconProp;
  urlPath: string;
  sx?: SxProps<Theme>;
  hideDate?: boolean;
}

export function AccordionPreviewCardGrid({
  previews,
  urlLabel,
  icon,
  urlPath,
  sx,
  hideDate,
}: Props) {
  const previewsByMonth = useMemo(() => {
    return previews.data
      .sort((a, b) => {
        return (
          new Date(b?.attributes?.preview?.date ?? 0).getTime() -
          new Date(a?.attributes?.preview?.date ?? 0).getTime()
        );
      })
      .reduce(
        (acc, data) => {
          const preview = data?.attributes?.preview;
          if (!preview) return acc;

          const date = new Date(preview.date);
          const month = date.toLocaleString('default', { month: 'long' });

          if (!acc[month]) {
            acc[month] = [];
          }

          acc[month].push(data);
          return acc;
        },
        {} as Record<string, typeof previews.data>
      );
  }, [previews]);
  const AccordionsByPreview = useMemo(() => {
    const result: JSX.Element[] = [];
    Object.entries(previewsByMonth).forEach((item, index) => {
      const accordion = (
        <Accordion defaultExpanded key={item[0]} elevation={0}>
          <AccordionSummary
            aria-controls={`panel-content-${item[0]}`}
            id={`accordion-header-${item[0]}`}
            expandIcon={<FontAwesomeIcon icon={icons.downArrow} />}
          >
            <Typography variant="h3" component="h3" marginBottom={0} fontWeight={400}>
              {item[0]}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            id={`panel-content-${item[0]}`}
            aria-labelledby={`accordion-header-${item[0]}`}
          >
            <Grid container spacing={4}>
              {item[1].map(data => {
                const preview = data?.attributes?.preview;
                if (!preview) return null;
                return (
                  <Grid item xs={12} md={6} lg={4} key={data.id}>
                    <PreviewCard
                      title={data.attributes.title}
                      description={preview.description}
                      externalUrlLink={preview.externalUrlLink}
                      internalDocumentLink={preview.internalDocumentLink}
                      pageUrlLink={`${urlPath}/${data.attributes.slug}`}
                      image={preview.image}
                      urlLabel={urlLabel}
                      icon={icon}
                      date={preview.date}
                      sx={sx}
                      hideDate={hideDate}
                      subHeader={preview.subHeader}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>
      );
      result.push(accordion);
    });
    return result;
  }, [hideDate, icon, previewsByMonth, sx, urlLabel, urlPath]);
  return AccordionsByPreview;
}
